import { RouteConfig } from 'vue-router'
import { RoutesList } from '@/interfaces/interfaces'
import auth from '@/router/middleware/auth'
import guest from '@/router/middleware/guest'
import { _CONST } from '@/constants/constants'

const metaAuth = { middleware: [auth] }
const metaGuest = { middleware: [guest] }

const routes: RouteConfig[] = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/auth',
    component: () => import('layouts/AuthLayout.vue'),
    children: [{ path: '', name: RoutesList.auth, component: () => import('pages/Auth.vue'), meta: metaGuest }],
  },

  {
    path: '/webauthn-init',
    component: () => import('layouts/AuthLayout.vue'),
    children: [
      {
        path: ':username/:password',
        name: RoutesList.webauthnInit,
        component: () => import('pages/WebAuthnInit.vue'),
        meta: metaGuest,
      },
    ],
  },
  {
    path: '/blank',
    component: () => import('layouts/MainLayout.vue'),
    children: [{ path: '', name: RoutesList.blank, component: () => import('pages/Blank.vue'), meta: metaAuth }],
  },
  {
    path: '/preloader',
    name: RoutesList.preloader,
    component: () => import('pages/Preloader.vue'),
  },
  {
    path: '/dashboard',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      { path: '', name: RoutesList.dashboard, component: () => import('pages/Dashboard.vue'), meta: { ...metaAuth } },
    ],
  },
  {
    path: '/verifications',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: 'card',
        name: RoutesList.cardVerifications,
        component: () => import('pages/CardVerifications.vue'),
        meta: { ...metaAuth, parent: RoutesList.verifications, permissions: [_CONST.permissions.Verification.Cards] },
      },
      {
        path: 'card/:cardId',
        name: RoutesList.cardVerification,
        meta: { ...metaAuth, parent: RoutesList.verifications, permissions: [_CONST.permissions.Verification.Cards] },
        component: () => import('@pages/CardVerification.vue'),
      },
      {
        path: 'user',
        name: RoutesList.userVerifications,
        component: () => import('pages/UserVerifications.vue'),
        meta: {
          ...metaAuth,
          parent: RoutesList.verifications,
          permissions: [_CONST.permissions.Verification.PersonalInfo],
        },
      },
      {
        path: 'user/:userId',
        name: RoutesList.userVerification,
        meta: {
          ...metaAuth,
          parent: RoutesList.verifications,
          permissions: [_CONST.permissions.Verification.PersonalInfo],
        },
        component: () => import('@pages/UserVerification.vue'),
      },
    ],
  },
  {
    path: '/kyt',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      { path: '', name: RoutesList.kytList, component: () => import('@pages/KytList.vue'), meta: { ...metaAuth } },
    ],
  },
  {
    path: '/clients',
    component: () => import('layouts/MainLayout.vue'),
    name: RoutesList.clients,
    children: [
      {
        path: '',
        name: RoutesList.accounts,
        component: () => import('@pages/Clients.vue'),
        meta: { ...metaAuth, parent: RoutesList.clients, permissions: [_CONST.permissions.Accounts.Clients.self] },
      },
      {
        path: ':accountId',
        name: RoutesList.account,
        meta: { ...metaAuth, parent: RoutesList.clients, permissions: [_CONST.permissions.Accounts.Clients.self] },
        component: () => import('@pages/Client.vue'),
      },
    ],
  },
  {
    path: '/quests',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: RoutesList.quests,
        component: () => import('@pages/SecurityQuests.vue'),
        meta: { ...metaAuth, permissions: [_CONST.permissions.Accounts.SecurityQuests.self] },
      },
      {
        path: ':questId',
        name: RoutesList.quest,
        component: () => import('pages/SecurityQuest.vue'),
        meta: { ...metaAuth, permissions: [_CONST.permissions.Accounts.SecurityQuests.self] },
      },
    ],
  },
  {
    path: '/admins',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: RoutesList.admins,
        component: () => import('@pages/Admins.vue'),
        meta: { ...metaAuth, permissions: [_CONST.permissions.Accounts.Admins.self] },
      },
      {
        path: 'log',
        name: RoutesList.adminLog,
        component: () => import('@pages/AdminLog.vue'),
        meta: { ...metaAuth, parent: RoutesList.admins, permissions: [_CONST.permissions.Accounts.Admins.self] },
      },
    ],
  },
  {
    path: '/orders',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: RoutesList.orders,
        component: () => import('pages/Orders.vue'),
        meta: { ...metaAuth, permissions: [_CONST.permissions.Orders.self] },
      },
      {
        path: ':orderId',
        name: RoutesList.order,
        meta: { ...metaAuth, parent: RoutesList.orders, permissions: [_CONST.permissions.Orders.self] },
        component: () => import('@pages/Order.vue'),
      },
    ],
  },
  {
    path: '/matcher-orders',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: RoutesList.matcherOrders,
        component: () => import('pages/MatcherOrders.vue'),
        meta: { ...metaAuth, permissions: [_CONST.permissions.MatcherOrders] },
      },
    ],
  },
  {
    path: '/trades',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: RoutesList.trades,
        component: () => import('pages/Trades.vue'),
        meta: { ...metaAuth, permissions: [_CONST.permissions.Fills] },
      },
      {
        path: ':tradeId',
        name: RoutesList.trade,
        meta: { ...metaAuth, parent: RoutesList.trades, permissions: [_CONST.permissions.Fills] },
        component: () => import('pages/Trade.vue'),
      },
    ],
  },
  {
    path: '/ledger',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: RoutesList.ledger,
        component: () => import('pages/Ledger.vue'),
        meta: { ...metaAuth, permissions: [_CONST.permissions.Ledger] },
      },
      {
        path: 'withdrawal/:withdrawalId',
        name: RoutesList.withdrawal,
        meta: { ...metaAuth, parent: RoutesList.ledger, permissions: [_CONST.permissions.Ledger] },
        component: () => import('pages/Withdrawal.vue'),
      },
      {
        path: 'deposit/:depositId',
        name: RoutesList.deposit,
        meta: { ...metaAuth, parent: RoutesList.ledger, permissions: [_CONST.permissions.Ledger] },
        component: () => import('pages/Deposit.vue'),
      },
      {
        path: 'admin-record/:adminRecordId',
        name: RoutesList.adminRecord,
        meta: { ...metaAuth, parent: RoutesList.ledger, permissions: [_CONST.permissions.Ledger] },
        component: () => import('pages/AdminRecord.vue'),
      },
    ],
  },
  {
    path: '/funding',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: RoutesList.funding,
        component: () => import('pages/Funding.vue'),
        meta: { ...metaAuth, permissions: [_CONST.permissions.Funding.self] },
      },
    ],
  },
  {
    path: '/withdrowal-confirm',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: RoutesList.withdrowalConfirm,
        component: () => import('pages/WithdrowalConfirm.vue'),
        meta: { ...metaAuth, permissions: [_CONST.permissions.Funding.self] },
      },
    ],
  },
  {
    path: '/positions',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: RoutesList.positions,
        component: () => import('pages/Positions.vue'),
        meta: { ...metaAuth, permissions: [_CONST.permissions.Position] },
      },
      {
        path: ':symbol',
        name: RoutesList.position,
        meta: { ...metaAuth, parent: RoutesList.positions, permissions: [_CONST.permissions.Position] },
        component: () => import('@pages/Position.vue'),
      },
    ],
  },
  {
    path: '/daily-positions',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: RoutesList.dailyPositions,
        component: () => import('pages/DailyPositions.vue'),
        meta: { ...metaAuth, permissions: [_CONST.permissions.Position] },
      },
    ],
  },
  {
    path: '/management-accounting/:currency?',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: ':lp?',
        name: RoutesList.managementAccounting,
        component: () => import('pages/ManagementAccounting.vue'),
        meta: { ...metaAuth, permissions: [_CONST.permissions.ManagmentAccounting.self] },
      },
    ],
  },
  {
    path: '/market-management',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: RoutesList.marketManagement,
        component: () => import('pages/MarketManagement.vue'),
        meta: { ...metaAuth, permissions: [_CONST.permissions.Settings.CurrenciesAndSymbolsEdit] },
      },
      {
        path: ':symbol',
        name: RoutesList.marketManagementSymbol,
        meta: {
          ...metaAuth,
          parent: RoutesList.marketManagement,
          permissions: [_CONST.permissions.Settings.CurrenciesAndSymbolsEdit],
        },
        component: () => import('pages/MarketManagementSymbol.vue'),
      },
    ],
  },
  {
    path: '/management-accounting-clearing-history',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: ':gateway',
        name: RoutesList.clearingHistory,
        component: () => import('pages/ClearingHistory.vue'),
        meta: { ...metaAuth, permissions: [_CONST.permissions.Funding.ClearingHistory] },
      },
    ],
  },
  {
    path: '/management-accounting-clearing-details',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: ':clearingId',
        name: RoutesList.clearingDetails,
        component: () => import('pages/ClearingDetails.vue'),
        meta: { ...metaAuth, permissions: [_CONST.permissions.Funding.ClearingHistory] },
      },
    ],
  },
  {
    path: '/management-accounting-deposit-recipients',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: ':gateway',
        name: RoutesList.depositRecipients,
        component: () => import('pages/DepositRecipients.vue'),
        meta: { ...metaAuth, permissions: [_CONST.permissions.ManagmentAccounting.self] },
      },
    ],
  },
  {
    path: '/counterparties/:currency?',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: ':lp?',
        name: RoutesList.counterparties,
        component: () => import('pages/Counterparties.vue'),
        meta: { ...metaAuth, permissions: [_CONST.permissions.ManagmentAccounting.self] },
      },
    ],
  },
  {
    path: '/client-balances/',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: RoutesList.clientBalances,
        component: () => import('pages/ClientsBalances.vue'),
        meta: { ...metaAuth, permissions: [_CONST.permissions.ManagmentAccounting.self] },
      },
    ],
  },
  {
    path: '/settings',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: 'main',
        name: RoutesList.main,
        meta: { ...metaAuth, parent: RoutesList.settings, permissions: [_CONST.permissions.Settings.SystemConfig] },
        component: () => import('@pages/Settings.vue'),
      },
      {
        path: 'lp-settings',
        name: RoutesList.lpSettings,
        meta: { ...metaAuth, parent: RoutesList.settings, permissions: [_CONST.permissions.Settings.SystemConfig] },
        component: () => import('@pages/LPSettings.vue'),
      },
      {
        path: 'asset-processing',
        name: RoutesList.assetProcessing,
        meta: { ...metaAuth, parent: RoutesList.settings, permissions: [_CONST.permissions.Settings.AssetProcessing] },
        component: () => import('@pages/AssetProcessing.vue'),
      },
      {
        path: 'currencies',
        name: RoutesList.currencies,
        meta: {
          ...metaAuth,
          parent: RoutesList.settings,
          permissions: [_CONST.permissions.Settings.CurrenciesAndSymbolsEdit],
        },
        component: () => import('pages/Currencies.vue'),
      },
      {
        path: 'fees',
        name: RoutesList.fees,
        meta: { ...metaAuth, parent: RoutesList.settings, permissions: [_CONST.permissions.Settings.Fees] },
        component: () => import('pages/Fees.vue'),
      },
      {
        path: 'limits',
        name: RoutesList.limits,
        meta: { ...metaAuth, parent: RoutesList.settings, permissions: [_CONST.permissions.Settings.Limits] },
        component: () => import('pages/Limits.vue'),
      },
      {
        path: 'liquidity-settings',
        name: RoutesList.liquiditySettings,
        meta: { ...metaAuth, parent: RoutesList.settings, permissions: [_CONST.permissions.Settings.Liquidity] },
        component: () => import('pages/LiquiditySettings.vue'),
      },
    ],
  },
  {
    path: '/html-pages',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: RoutesList.htmlPages,
        component: () => import('pages/HtmlPages.vue'),
        meta: { ...metaAuth, permissions: [_CONST.permissions.HtmlPages.self] },
      },
      {
        path: 'page/:pageId?',
        name: RoutesList.htmlPage,
        meta: { ...metaAuth, parent: RoutesList.htmlPages, permissions: [_CONST.permissions.HtmlPages.self] },
        component: () => import('@pages/HtmlPage.vue'),
      },
    ],
  },
  {
    path: '/exchangers-comparison',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: RoutesList.exchangersComparison,
        component: () => import('pages/ExchangersComparison.vue'),
        meta: { ...metaAuth, permissions: [_CONST.permissions.ManagmentAccounting.self] },
      },
    ],
  },
  // Always leave this as last one,
  // but you can also remove it
  {
    path: '*',
    component: () => import('pages/Error404.vue'),
  },
]

export default routes
